import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { X, CheckCircle, XCircle, AlertCircle, Info } from 'lucide-react';
import { apiFetch } from '../api';

type ToastType = 'success' | 'error' | 'warning' | 'info';

interface Toast {
  id: string;
  notificationId?: number | string;
  title?: string;
  message: string;
  type: ToastType;
}

export interface DismissCriteria {
  id?: string | number;
  notificationId?: number | string;
  title?: string;
  message?: string;
}

interface ToastContextType {
  showToast: (message: string, type?: ToastType, title?: string, notificationId?: number | string) => void;
  dismissNotificationToasts: (criteria: DismissCriteria) => void;
  success: (message: string, title?: string) => void;
  error: (message: string, title?: string) => void;
  warning: (message: string, title?: string) => void;
  info: (message: string, title?: string) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
}

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const channelRef = useRef<BroadcastChannel | null>(null);

  const dismissNotificationToasts = useCallback((criteria: DismissCriteria) => {
    setToasts(prev =>
      prev.filter(t => {
        if (criteria.id && t.id === String(criteria.id)) return false;
        if (
          criteria.notificationId !== undefined &&
          t.notificationId !== undefined &&
          String(t.notificationId) === String(criteria.notificationId)
        ) {
          return false;
        }
        if (criteria.title && criteria.message && t.title === criteria.title && t.message === criteria.message) {
          return false;
        }
        if (!criteria.title && criteria.message && t.message === criteria.message) {
          return false;
        }
        return true;
      })
    );
  }, []);

  useEffect(() => {
    let bc: BroadcastChannel | null = null;
    try {
      if (typeof BroadcastChannel !== 'undefined') {
        bc = new BroadcastChannel('orgapilot_inapp_sync');
        channelRef.current = bc;
        bc.onmessage = (event) => {
          if (event?.data) {
            dismissNotificationToasts(event.data);
          }
        };
      }
    } catch {}

    const handleStorage = (e: StorageEvent) => {
      if (e.key === 'orgapilot_dismiss_sync' && e.newValue) {
        try {
          const data = JSON.parse(e.newValue);
          dismissNotificationToasts(data);
        } catch {}
      }
    };
    window.addEventListener('storage', handleStorage);

    return () => {
      if (bc) {
        try { bc.close(); } catch {}
      }
      channelRef.current = null;
      window.removeEventListener('storage', handleStorage);
    };
  }, [dismissNotificationToasts]);

  const showToast = useCallback((message: string, type: ToastType = 'info', title?: string, notificationId?: number | string) => {
    const id = Math.random().toString(36).substr(2, 9);
    const toast: Toast = { id, notificationId, title, message, type };
    
    setToasts(prev => [...prev, toast]);
    
    // Auto-remove after 5 seconds
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, 5000);
  }, []);

  const success = useCallback((message: string, title?: string) => showToast(message, 'success', title), [showToast]);
  const error = useCallback((message: string, title?: string) => showToast(message, 'error', title), [showToast]);
  const warning = useCallback((message: string, title?: string) => showToast(message, 'warning', title), [showToast]);
  const info = useCallback((message: string, title?: string) => showToast(message, 'info', title), [showToast]);

  const removeToast = (toast: Toast) => {
    const payload = {
      id: toast.id,
      notificationId: toast.notificationId,
      title: toast.title,
      message: toast.message,
      ts: Date.now(),
    };
    dismissNotificationToasts(payload);

    try {
      channelRef.current?.postMessage(payload);
    } catch {}
    try {
      localStorage.setItem('orgapilot_dismiss_sync', JSON.stringify(payload));
    } catch {}

    if (toast.notificationId) {
      apiFetch(`/system-notifications/inapp/${toast.notificationId}/dismiss`, {
        method: 'POST',
      }).catch(() => {});
    }
  };

  const getIcon = (type: ToastType) => {
    switch (type) {
      case 'success':
        return <CheckCircle className="w-5 h-5" />;
      case 'error':
        return <XCircle className="w-5 h-5" />;
      case 'warning':
        return <AlertCircle className="w-5 h-5" />;
      case 'info':
        return <Info className="w-5 h-5" />;
    }
  };

  const getColors = (type: ToastType) => {
    switch (type) {
      case 'success':
        return 'bg-green-600 text-white';
      case 'error':
        return 'bg-red-600 text-white';
      case 'warning':
        return 'bg-yellow-600 text-white';
      case 'info':
        return 'bg-blue-600 text-white';
    }
  };

  return (
    <ToastContext.Provider value={{ showToast, dismissNotificationToasts, success, error, warning, info }}>
      {children}
      
      {/* Toast Container */}
      <div className="fixed top-4 right-4 z-[9999] flex flex-col gap-2 pointer-events-none">
        {toasts.map((toast) => (
          <div
            key={toast.id}
            className={`${getColors(toast.type)} rounded-lg shadow-lg p-4 flex items-start gap-3 min-w-[300px] max-w-[500px] pointer-events-auto animate-slide-in`}
          >
            <div className="flex-shrink-0 mt-0.5">
              {getIcon(toast.type)}
            </div>
            <div className="flex-1 text-sm font-medium break-words">
              {toast.title && <div className="font-bold text-sm mb-0.5">{toast.title}</div>}
              <div>{toast.message}</div>
            </div>
            <button
              onClick={() => removeToast(toast)}
              className="flex-shrink-0 hover:opacity-70 transition-opacity"
              aria-label="Schließen"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}
